/*
  Ported from the portal's components/Cards/UpcomingLessonCard (the parent dashboard's purple card):
  same colours, status pills, 20px radius and "Join now" rules (the button shows only when the
  link, meeting id and passcode are all present). Adapted to the app's Lesson shape and to open
  Zoom in the right place on a phone.
*/
import { ClockCircleOutlined } from "@ant-design/icons";
import SubjectIcon from "app/components/Icons/SubjectIcon";
import { Button } from "app/components/v1/Button";
import STYLE_DEFAULTS from "app/scss/style";
import type { Lesson } from "app/services/types";
import { fmtTime, lessonPhase, local, minutesUntil, relativeDay, type LessonPhase } from "app/utils/time";
import { openExternal } from "app/native/nativeApp";
import { useEffect, useState } from "react";
import styled from "styled-components";

const Card = styled.div`
  border-radius: 20px;
  color: ${STYLE_DEFAULTS.MAIN_COLORS.white};
  background: ${STYLE_DEFAULTS.MAIN_COLORS.primary};
  padding: 16px;
  position: relative;
  overflow: hidden;

  &.live { background: ${STYLE_DEFAULTS.MAIN_COLORS.success}; .remain-time { background: ${STYLE_DEFAULTS.MAIN_COLORS.danger}; } .join-button { color: ${STYLE_DEFAULTS.MAIN_COLORS.success}; } }
  &.upcoming .remain-time { background: ${STYLE_DEFAULTS.MAIN_COLORS.secondary}; }
  &.soon .remain-time { background: ${STYLE_DEFAULTS.MAIN_COLORS.warning}; color: #2c2c39; }
  &.past { background: #92929d; }

  .card-upcoming-label { font-weight: 800; font-size: 1.125rem; letter-spacing: -0.6px; }
  .remain-time { display: inline-flex; align-items: center; gap: 5px; border-radius: 18px; padding: 5px 10px; font-size: 0.875rem; font-weight: 500; background: ${STYLE_DEFAULTS.MAIN_COLORS.danger}; }
  .name { font-weight: 800; font-size: 1.25rem; margin-top: 8px; letter-spacing: -0.4px; }
  .details-section-1 { font-weight: 700; font-size: 1rem; margin-top: 6px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .details-section-2 { font-weight: 600; font-size: 0.875rem; margin-top: 4px; opacity: 0.95; }
  .details-section-3 { margin-top: 6px; font-size: 1rem; .student-name-label { font-weight: 700; } }
  .card-subject-icon { position: absolute; top: 16px; right: 16px; width: 64px; height: 64px; }
  .card-main { padding-right: 80px; }
  .join { width: 100%; margin-top: 14px; display: flex; flex-direction: column; align-items: center; gap: 4px; .join-button { width: 100%; background: #fff; color: ${STYLE_DEFAULTS.MAIN_COLORS.primary}; } p { margin: 0; font-size: 0.875rem; color: rgba(255,255,255,0.85); font-family: var(--st-font-family-secondary); } }
`;

const dot = <span aria-hidden style={{ opacity: 0.7 }}>·</span>;

export function RemainTime({ start, phase }: { start: string; phase: LessonPhase }) {
  const mins = minutesUntil(start);
  return (
    <span className="remain-time">
      <ClockCircleOutlined />
      {phase === "live" ? <span>Happening now</span>
        : phase === "soon" ? <span>Starts in {mins < 1 ? "a moment" : mins < 60 ? `${mins} min` : local(start).fromNow(true)}</span>
        : phase === "past" ? <span>Finished</span>
        : <span>Upcoming</span>}
    </span>
  );
}

export default function UpcomingLessonCard({ lesson, studentNames, label = "Upcoming Lesson" }: { lesson: Lesson; studentNames: string[]; label?: string }) {
  const [phase, setPhase] = useState<LessonPhase>(() => lessonPhase(lesson.start, lesson.end));
  useEffect(() => {
    setPhase(lessonPhase(lesson.start, lesson.end));
    const id = setInterval(() => setPhase(lessonPhase(lesson.start, lesson.end)), 10000);
    return () => clearInterval(id);
  }, [lesson.start, lesson.end]);

  const showJoin = Boolean(lesson.zoomLink && lesson.zoomMeetingId && lesson.zoomMeetingPasscode) && !lesson.cancelled && phase !== "past";
  const classLabel = [lesson.year, lesson.subject, lesson.classId].filter(Boolean).join(" ");

  return (
    <Card className={lesson.cancelled ? "past" : phase}>
      <SubjectIcon className="card-subject-icon" name={lesson.subject} imageUrl={lesson.subjectImage} backgroundColor="white" />
      <div className="card-main">
        {lesson.cancelled ? <span className="remain-time" style={{ background: "#44444f" }}>Cancelled</span> : <RemainTime start={lesson.start} phase={phase} />}
        <div className="card-upcoming-label mt-2">{label}</div>
        <div className="name">{lesson.subject}</div>
        <div className="details-section-1">
          <span>{relativeDay(lesson.start)}</span>{dot}<span>{fmtTime(lesson.start)}</span><span>-</span><span>{fmtTime(lesson.end)}</span>
        </div>
        <div className="details-section-2">{classLabel}{lesson.teacher ? <> {dot} {lesson.teacher}</> : null}</div>
        {studentNames.length > 0 && (
          <div className="details-section-3"><span className="student-name-label">Student: </span><span>{studentNames.join(", ")}</span></div>
        )}
      </div>
      {!lesson.cancelled && phase !== "past" && (
        <div className="join">
          {showJoin ? (
            <>
              <Button className="join-button" buttonType="white" name="Join now" onClick={() => openExternal(lesson.zoomLink!)} />
              <p className="mt-2">Zoom ID: {lesson.zoomMeetingId}</p>
              <p>Password: {lesson.zoomMeetingPasscode}</p>
            </>
          ) : (
            <p>Link available soon</p>
          )}
        </div>
      )}
    </Card>
  );
}
